import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { DispatchDateService } from './dispatch-date.service';
import { Session } from '../interfaces';
import { DispatchZone, DispatchRoute } from '../models';

@Injectable({
  providedIn: 'root'
})
export class DispatchRouteService {
  constructor(
    private apiService: ApiService,
    private dispatchDateService: DispatchDateService
  ) {}

  readById(id: string): Observable<DispatchRoute[]> {
    return this.apiService
      .get(this.apiService.dispatchRoutes + '/' + id)
      .pipe(map((response: { routes: DispatchRoute[] }) => response.routes));
  }

  getDispatchRoutes(request){
    return this.apiService.post(`${this.apiService.dispatchRoutes}s`, request);
  }

  readByType(entityType: string) {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (
      session &&
      session.dispatchRoutes &&
      session.dispatchRoutes[entityType]
    ) {
      return of(session.dispatchRoutes[entityType]);
    } else {
      return this.apiService
        .get(
          this.apiService.dispatchRoutesByType.replace(
            '{{entityType}}',
            entityType
          )
        )
        .pipe(
          map((dispatchRoutes: DispatchRoute[]) => {
            if (!session.dispatchRoutes) {
              session.dispatchRoutes = {};
            }
            session.dispatchRoutes[entityType] = dispatchRoutes;
            this.setSession(session);
            return dispatchRoutes;
          })
        );
    }
  }

  update(data: any): Observable<DispatchRoute> {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    return this.apiService.put(this.apiService.dispatchRoutes, data).pipe(
      map(() => {
        const dispatchRoute: DispatchRoute = data;
        const configurationIndex = session.dispatchRoutes[
          dispatchRoute.origin.entityType
        ].findIndex((o, i) => o.id.toString() === dispatchRoute.id.toString());
        session.dispatchRoutes[dispatchRoute.origin.entityType][
          configurationIndex
        ] = dispatchRoute;
        this.setSession(session);
        return dispatchRoute;
      })
    );
  }

  readByEntity(entityCode: string): Observable<DispatchZone[]> {
    return (
      this.apiService
        .get(
          this.apiService.dispatchRoutesByEntity.replace(
            '{{entityCode}}',
            entityCode
          )
        )
        .pipe(
          map((response: { destinies: DispatchZone[] }) => response.destinies)
        )
    );
  }

  create(data: any): Observable<DispatchRoute[]> {
    return this.apiService.post(this.apiService.dispatchRoutes, data).pipe(
      map((response: { routes: DispatchRoute[] }) => response.routes),
      map((dispatchRoutes: DispatchRoute[]) => {
        const session: Session = JSON.parse(
          localStorage.getItem(environment.sessionAuth)
        );
        if (!session.dispatchRoutes) {
          session.dispatchRoutes = {};
        }
        session.dispatchRoutes.form = dispatchRoutes;
        delete session.dispatchRoutes[data.origin.entityType];
        this.setSession(session);
        return dispatchRoutes;
      })
    );
  }

  massEdit(data: { routeIds: number[]; carrierId: number; leadTime: number }): Observable<any> {
    return this.apiService
      .patch(this.apiService.dispatchRoutesMassEdit, data)
      .pipe(
        map(response => {
          this.dispatchDateService.removeSession();
          return response;
        })
      );
  }

  configure(data: any): Observable<any> {
    return this.apiService
      .post(this.apiService.dispatchRoutesConfigure, data)
      .pipe(
        map(response => {
          this.dispatchDateService.removeSession();
          return response;
        })
      );
  }

  getForm(): DispatchRoute[] {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    return session.dispatchRoutes.form || [];
  }

  setSession(session: Session) {
    localStorage.setItem(environment.sessionAuth, JSON.stringify(session));
  }

  removeSession(entityType: string) {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (session.dispatchRoutes && session.dispatchRoutes[entityType]) {
      delete session.dispatchRoutes[entityType];
      this.setSession(session);
    }
  }

  getDispatchZonesByEntities(request): Observable<any> {
    return this.apiService.post(`${this.apiService.dispatchRoutes}s/zones`, request);
  }

}
